import type { MRT_VisibilityState } from "material-react-table";
import type { FetchArgs, FetchBaseQueryError } from "@reduxjs/toolkit/query";
import { api } from "_shared/_stores/api";
import { logSchemaFailure } from "_shared/_utilities/log-schema-failure";
import {
  TableColumnPreferenceSchema,
  TableColumnPreferencesSchema,
  type ColumnVisibilityEntry,
  type TableColumnPreferenceJSON,
  type TableColumnPreferencesJSON,
} from "_shared/_views/tables/_schemas/table-column-preferences-schema";
import {
  currentPrincipal,
  hasPendingSave,
  joinSaveQueue,
  principalOf,
  untilSavesAnswered,
  withColumnVisibility,
  withPendingSaves,
} from "_shared/_views/tables/_api/table-column-save-queue";
import type Session from "users/__session/session";
import { serializeQueryArgsBySessionCredential } from "users/__session/session-query-cache-keys";

/**
 * The MRT shape and the wire shape of one table's choice.
 *
 * The wire carries a list rather than an object so that a column id reaches the server and comes
 * back exactly as it was written: the API camel-cases property names, and a column id is not one.
 */
export function toColumnVisibilityEntries(visibility: MRT_VisibilityState): ColumnVisibilityEntry[] {
  return Object.entries(visibility).map(([columnId, visible]) => ({ columnId, visible }));
}

export function fromColumnVisibilityEntries(columns: readonly ColumnVisibilityEntry[]): MRT_VisibilityState {
  return Object.fromEntries(columns.map((column) => [column.columnId, column.visible]));
}

type SaveTableColumnVisibilityArgs = {
  session: Session;
  tableKey: string;
  visibility: MRT_VisibilityState;
};

/** Writes the saved choice into the cached answer, so a table opened next reads it without asking again. */
function withTable(
  draft: TableColumnPreferencesJSON,
  tableKey: string,
  columns: ColumnVisibilityEntry[],
): void {
  const index = draft.tables.findIndex((table) => table.tableKey === tableKey);
  if (index >= 0) draft.tables[index] = withColumnVisibility(draft.tables[index], columns);
  else draft.tables.push({ tableKey, columns, updatedAt: new Date().toISOString() });
}

type ResetTableColumnVisibilityArgs = {
  session: Session;
  tableKey: string;
};

type RequestAnswer = { data?: unknown; error?: FetchBaseQueryError };

/** The endpoint's base query, as a `queryFn` is handed it. */
type SaveRequest = (args: FetchArgs) => RequestAnswer | PromiseLike<RequestAnswer>;

/** What a request bound to someone who is no longer signed in answers, without being sent. */
const PRINCIPAL_CHANGED: FetchBaseQueryError = {
  status: "CUSTOM_ERROR",
  error: "The person who made this change is no longer the one signed in, so it was not sent.",
};

/**
 * Sends one save in its table's turn (see `table-column-save-queue.ts`).
 *
 * At module level rather than inside the endpoint, so the `try`/`finally` that gives the queue back
 * its place is plain code, whatever the endpoint is compiled into.
 */
async function saveInTurn(
  args: SaveTableColumnVisibilityArgs,
  send: SaveRequest,
): Promise<{ data: TableColumnPreferenceJSON } | { error: FetchBaseQueryError }> {
  const columns = toColumnVisibilityEntries(args.visibility);
  const place = joinSaveQueue(args.tableKey, columns, principalOf(args.session));
  try {
    await place.turn;
    // A newer save of this table is waiting behind this one, and it carries the whole choice.
    if (!place.isNewest()) {
      return { data: { tableKey: args.tableKey, columns, updatedAt: new Date().toISOString() } };
    }
    if (principalOf(args.session) !== currentPrincipal()) return { error: PRINCIPAL_CHANGED };
    const result = await send({
      url: `users/me/table-preferences/${encodeURIComponent(args.tableKey)}`,
      method: "PUT",
      body: { columns },
    });
    return result.error ? { error: result.error } : { data: result.data as TableColumnPreferenceJSON };
  } finally {
    place.finish();
  }
}

/** Sends a reset once the saves it must come after have their answers, for the person who asked. */
async function resetAfterSaves(
  session: Session,
  tableKey: string | undefined,
  send: SaveRequest,
): Promise<{ data: null } | { error: FetchBaseQueryError }> {
  await untilSavesAnswered(tableKey);
  if (principalOf(session) !== currentPrincipal()) return { error: PRINCIPAL_CHANGED };
  const url =
    tableKey === undefined ? "users/me/table-preferences" : `users/me/table-preferences/${encodeURIComponent(tableKey)}`;
  const result = await send({ url, method: "DELETE" });
  return result.error ? { error: result.error } : { data: null };
}

/** Whether a request succeeded. Its failure is the caller's to report, through `unwrap`. */
async function succeeded(queryFulfilled: Promise<unknown>): Promise<boolean> {
  try {
    await queryFulfilled;
    return true;
  } catch {
    return false;
  }
}

export const tableColumnPreferencesApi = api.injectEndpoints({
  endpoints: (build) => ({
    getTableColumnPreferences: build.query<TableColumnPreferencesJSON, { session: Session }>({
      query: ({ session }) => ({
        url: "users/me/table-preferences",
        headers: { Authorization: `Bearer ${session.authToken?.value}` },
      }),
      serializeQueryArgs: serializeQueryArgsBySessionCredential,
      rawResponseSchema: TableColumnPreferencesSchema,
      onSchemaFailure: logSchemaFailure,
      // Asked for again when the person comes back to the app or opens a table, so a choice made or
      // reset in another browser reaches this one; a save still on its way here stays in the answer.
      transformResponse: (answer: TableColumnPreferencesJSON, _meta, { session }) =>
        withPendingSaves(answer, principalOf(session)),
    }),
    saveTableColumnVisibility: build.mutation<TableColumnPreferenceJSON, SaveTableColumnVisibilityArgs>({
      queryFn: (args, _api, _extraOptions, baseQuery) => saveInTurn(args, baseQuery),
      responseSchema: TableColumnPreferenceSchema,
      onSchemaFailure: logSchemaFailure,
      // Not a tag invalidation: refetching every table's choice after each switch in the menu would
      // be a round trip for an answer this browser already has. The newest choice goes into the
      // cached answer at once, and the saves before it can no longer overwrite it on the server.
      onQueryStarted: ({ session, tableKey, visibility }, { dispatch }) => {
        dispatch(
          tableColumnPreferencesApi.util.updateQueryData("getTableColumnPreferences", { session }, (draft) =>
            withTable(draft, tableKey, toColumnVisibilityEntries(visibility)),
          ),
        );
      },
    }),
    resetTableColumnVisibility: build.mutation<null, ResetTableColumnVisibilityArgs>({
      // Sent only once every save of this table has its answer, so the reset is the last word.
      queryFn: ({ session, tableKey }, _api, _extraOptions, baseQuery) => resetAfterSaves(session, tableKey, baseQuery),
      // Forgets the table's current view in the cached answer only once the server has, so a reset
      // that failed does not show the table as reset. A table opened next in this tab then reads "no
      // choice" and shows its default columns. Its saved views stay, as they do on the server (#6714).
      onQueryStarted: async ({ session, tableKey }, { dispatch, queryFulfilled }) => {
        if (!(await succeeded(queryFulfilled))) return;
        if (hasPendingSave(tableKey)) {
          refetchChoices(dispatch, session);
          return;
        }
        dispatch(
          tableColumnPreferencesApi.util.updateQueryData("getTableColumnPreferences", { session }, (draft) => {
            draft.tables = withoutCurrentViews(draft.tables, (table) => table.tableKey === tableKey);
          }),
        );
      },
    }),
    resetAllTableColumnVisibility: build.mutation<null, { session: Session }>({
      queryFn: ({ session }, _api, _extraOptions, baseQuery) => resetAfterSaves(session, undefined, baseQuery),
      onQueryStarted: async ({ session }, { dispatch, queryFulfilled }) => {
        if (!(await succeeded(queryFulfilled))) return;
        if (hasPendingSave()) {
          refetchChoices(dispatch, session);
          return;
        }
        dispatch(
          tableColumnPreferencesApi.util.updateQueryData("getTableColumnPreferences", { session }, (draft) => {
            draft.tables = withoutCurrentViews(draft.tables, () => true);
          }),
        );
      },
    }),
  }),
});

/**
 * The cached tables with the current view of each `reset` names forgotten, as a reset leaves them on
 * the server: a table with saved views keeps them, and one with none is gone.
 */
export function withoutCurrentViews(
  tables: TableColumnPreferenceJSON[],
  reset: (table: TableColumnPreferenceJSON) => boolean,
): TableColumnPreferenceJSON[] {
  return tables.flatMap((table) => {
    if (!reset(table)) return [table];
    if ((table.views ?? []).length === 0) return [];
    return [{ ...table, columns: [], currentView: null }];
  });
}

/**
 * A save the person made while the reset was on its way is a newer choice than the reset, and the
 * server may hold it. The cached answer cannot know which came last, so it is asked for again.
 */
function refetchChoices(dispatch: (action: unknown) => unknown, session: Session): void {
  dispatch(
    tableColumnPreferencesApi.endpoints.getTableColumnPreferences.initiate(
      { session },
      { forceRefetch: true, subscribe: false },
    ),
  );
}

export const {
  useGetTableColumnPreferencesQuery,
  useResetAllTableColumnVisibilityMutation,
  useResetTableColumnVisibilityMutation,
  useSaveTableColumnVisibilityMutation,
} = tableColumnPreferencesApi;
